<template>
    <b-form>
        <div class="reconnect-section-header">
            <h5>{{ $t('equipment.reconnect.policy') }}</h5>
        </div>
        <p class="reconnect-section-help">{{ $t('equipment.reconnect.policyHelp') }}</p>
        <b-row class="align-items-end">
            <b-col cols="auto">
                <b-form-group :label="$t('equipment.reconnect.unlimitedRetries')" :label-for="id('unlimitedRetries')">
                    <div class="reconnect-switch">
                        <b-form-checkbox
                            :id="id('unlimitedRetries')"
                            switch
                            v-model="unlimitedRetries"
                            :disabled="!canWrite"
                            :class="{ disabled: !canWrite }"
                            class="my-0"
                        />
                    </div>
                </b-form-group>
            </b-col>
            <b-col v-if="showRetryCount" cols="3">
                <b-form-group :label="$t('equipment.reconnect.maxRetry')" :label-for="id('maxRetry')">
                    <b-form-input
                        :id="id('maxRetry')"
                        type="number"
                        min="0"
                        max="500"
                        v-model="maxRetry"
                        :disabled="!canWrite"
                        :class="{ disabled: !canWrite }"
                        @change="onMaxRetryChange"
                    />
                </b-form-group>
            </b-col>
            <b-col v-else cols="auto">
                <b-form-group :label="$t('equipment.reconnect.useExponentialBackoff')" :label-for="id('exponential')">
                    <div class="reconnect-switch">
                        <b-form-checkbox
                            :id="id('exponential')"
                            switch
                            v-model="useExponentialBackoff"
                            :disabled="!canWrite"
                            :class="{ disabled: !canWrite }"
                            class="my-0"
                        />
                    </div>
                </b-form-group>
            </b-col>
            <b-col cols="3">
                <b-form-group :label="delayLabel" :label-for="id('delay')">
                    <b-input-group class="reconnect-delay-group" :append="$t('Seconds')" style="color: var(--text-color)">
                        <b-form-input
                            v-if="showMaxDelay"
                            :id="id('delay')"
                            type="number"
                            min="30"
                            max="3600"
                            v-model="maxDelayRetry"
                            :disabled="!canWrite"
                            :class="{ disabled: !canWrite }"
                            @change="onMaxDelayRetryChange"
                        />
                        <b-form-input
                            v-else
                            :id="id('delay')"
                            type="number"
                            min="1"
                            max="3600"
                            v-model="retryDelay"
                            :disabled="!canWrite"
                            :class="{ disabled: !canWrite }"
                            @change="onRetryDelayChange"
                        />
                    </b-input-group>
                </b-form-group>
            </b-col>
        </b-row>
        <div class="reconnect-section-header">
            <h5>{{ $t('equipment.reconnect.schedule') }}</h5>
            <b-form-checkbox
                switch
                v-model="scheduleVisible"
                :disabled="!canWrite"
                :class="{ disabled: !canWrite }"
                class="ml-3 my-0"
                :aria-label="$t('equipment.reconnect.schedule')"
            />
        </div>
        <p class="reconnect-section-help">{{ $t('equipment.reconnect.scheduleHelp') }}</p>
        <div v-if="scheduleVisible" class="reconnect-schedule-section">
            <b-row>
                <b-col cols="3">
                    <b-form-group :label="$t('equipment.reconnect.schedule.kind')" :label-for="id('scheduleKind')">
                        <b-form-select
                            :id="id('scheduleKind')"
                            v-model="scheduleKind"
                            :options="scheduleKindOptions"
                            :disabled="!canWrite"
                            :class="{ disabled: !canWrite }"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
            <DxDataGrid
                :key="'reconnect-windows-' + scheduleKind"
                :data-source="scheduleWindows"
                :allow-column-reordering="true"
                :repaint-changes-only="true"
                :show-borders="true"
                :selection="{ mode: 'single' }"
                :column-auto-width="true"
                :allow-column-resizing="true"
                :paging="{ enabled: false }"
                @init-new-row="onInitNewRow"
                @row-removing="onRowRemoving"
                @editor-preparing="onEditorPreparing"
            >
                <DxEditing
                    :allow-updating="canWrite"
                    :allow-adding="canWrite"
                    :allow-deleting="canWrite && scheduleWindows.length > 1"
                    :use-icons="true"
                    mode="row"
                />
                <DxColumn
                    v-if="isWeeklySchedule"
                    data-field="day"
                    data-type="number"
                    :caption="$t('equipment.reconnect.day')"
                    :allow-editing="canWrite"
                    alignment="left"
                    :validation-rules="[{ type: 'required' }]"
                >
                    <DxLookup :data-source="weekDayOptions" value-expr="value" display-expr="text" />
                </DxColumn>
                <DxColumn
                    data-field="start"
                    data-type="string"
                    :caption="$t('equipment.reconnect.start')"
                    :allow-editing="canWrite"
                    alignment="center"
                    :validation-rules="windowTimeValidationRules"
                />
                <DxColumn
                    data-field="end"
                    data-type="string"
                    :caption="$t('equipment.reconnect.end')"
                    :allow-editing="canWrite"
                    alignment="center"
                    :validation-rules="windowTimeValidationRules"
                />
                <DxColumn
                    v-if="isWeeklySchedule"
                    data-field="allDay"
                    data-type="boolean"
                    :caption="$t('equipment.reconnect.allDay')"
                    :allow-editing="canWrite"
                    :set-cell-value="setAllDayCellValue"
                    alignment="center"
                />
            </DxDataGrid>
        </div>
    </b-form>
</template>

<script>
import { DxDataGrid, DxColumn, DxLookup, DxEditing } from 'devextreme-vue/data-grid';
import {
    buildReconnectPayload,
    defaultDailyWindows,
    defaultWeeklyWindows,
    initReconnectState,
    isAllDayWindow,
    normalizeTimeInput,
    reconnectWindowsInvalid,
    windowTimesValid,
} from '@/utils/reconnectSchedule';

/**
 * Formulaire de l'onglet Reconnexion, réutilisable (Modbus, OPC UA, OPC DA, ...).
 * Dans un b-tabs : envelopper dans un b-tab, passer :value="equipment.Reconnect"
 * et récupérer le résultat via payload() à la sauvegarde.
 */
export default {
    name: 'Configuration_Reconnect',
    components: {
        DxDataGrid,
        DxColumn,
        DxLookup,
        DxEditing,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
    },
    data() {
        const reconnectState = initReconnectState(this.value);
        return {
            unlimitedRetries: reconnectState.unlimitedRetries,
            useExponentialBackoff: reconnectState.useExponentialBackoff,
            maxRetry: reconnectState.maxRetry,
            retryDelay: reconnectState.retryDelay,
            maxDelayRetry: reconnectState.maxDelayRetry,
            scheduleKind: reconnectState.scheduleKind,
            scheduleVisible: Number(reconnectState.scheduleKind) !== 0,
            dailyWindows: reconnectState.dailyWindows,
            weeklyWindows: reconnectState.weeklyWindows,
            scheduleKindOptions: [
                { value: 1, text: this.$t('equipment.reconnect.schedule.daily') },
                { value: 2, text: this.$t('equipment.reconnect.schedule.weekly') },
            ],
            weekDayOptions: [
                { value: 1, text: this.$t('triggers.planning.days.monday') },
                { value: 2, text: this.$t('triggers.planning.days.tuesday') },
                { value: 3, text: this.$t('triggers.planning.days.wednesday') },
                { value: 4, text: this.$t('triggers.planning.days.thursday') },
                { value: 5, text: this.$t('triggers.planning.days.friday') },
                { value: 6, text: this.$t('triggers.planning.days.saturday') },
                { value: 0, text: this.$t('triggers.planning.days.sunday') },
            ],
        };
    },
    computed: {
        canWrite() {
            return this.$store.state.auth.user.permissions.PermissionWrite;
        },
        isWeeklySchedule() {
            return Number(this.scheduleKind) === 2;
        },
        showRetryCount() {
            return !this.unlimitedRetries;
        },
        showMaxDelay() {
            return this.unlimitedRetries && this.useExponentialBackoff;
        },
        delayLabel() {
            return this.showMaxDelay
                ? this.$t('equipment.reconnect.maxDelay')
                : this.$t('equipment.reconnect.retryDelay');
        },
        scheduleWindows() {
            return this.isWeeklySchedule ? this.weeklyWindows : this.dailyWindows;
        },
        windowTimeValidationRules() {
            return [
                {
                    type: 'custom',
                    reevaluate: true,
                    message: this.$t('equipment.reconnect.invalidWindowEqual'),
                    validationCallback: (options) => {
                        const data = options.data || {};
                        if (this.isWeeklySchedule && isAllDayWindow(data)) {
                            return true;
                        }
                        const start = options.dataField === 'start' ? options.value : data.start;
                        const end = options.dataField === 'end' ? options.value : data.end;
                        return windowTimesValid(start, end, false);
                    },
                },
            ];
        },
        invalid() {
            if (!this.scheduleVisible) {
                return false;
            }
            return reconnectWindowsInvalid(this.scheduleKind, this.dailyWindows, this.weeklyWindows);
        },
    },
    watch: {
        scheduleKind(kind) {
            if (Number(kind) === 1 && this.dailyWindows.length === 0) {
                this.dailyWindows = defaultDailyWindows();
            }
            if (Number(kind) === 2 && this.weeklyWindows.length === 0) {
                this.weeklyWindows = defaultWeeklyWindows();
            }
        },
        scheduleVisible(visible) {
            if (visible && Number(this.scheduleKind) === 0) {
                this.scheduleKind = 1;
            }
        },
        invalid: {
            immediate: true,
            handler(value) {
                this.$emit('invalid-change', value);
            },
        },
    },
    methods: {
        id(suffix) {
            return `reconnect-${this._uid}-${suffix}`;
        },
        onMaxRetryChange(value) {
            if (value == null || value === '') {
                this.maxRetry = 0;
            } else if (value > 500) {
                this.maxRetry = 500;
            } else if (value < 0) {
                this.maxRetry = 0;
            }
        },
        onMaxDelayRetryChange(value) {
            if (value == null || value === '') {
                this.maxDelayRetry = 30;
            } else if (value > 3600) {
                this.maxDelayRetry = 3600;
            } else if (value < 30) {
                this.maxDelayRetry = 30;
            }
        },
        onRetryDelayChange(value) {
            if (value == null || value === '') {
                this.retryDelay = 5;
            } else if (value > 3600) {
                this.retryDelay = 3600;
            } else if (value < 1) {
                this.retryDelay = 1;
            }
        },
        onInitNewRow(e) {
            e.data.start = '08:00';
            e.data.end = '18:00';
            if (this.isWeeklySchedule) {
                e.data.day = 1;
                e.data.allDay = false;
            }
        },
        onRowRemoving(e) {
            if (this.scheduleWindows.length <= 1) {
                e.cancel = true;
            }
        },
        timeTextToDate(value) {
            const normalized = normalizeTimeInput(value);
            const match = String(normalized).match(/^(\d{2}):(\d{2})$/);
            if (!match) {
                return new Date(1970, 0, 1, 8, 0);
            }
            return new Date(1970, 0, 1, Number(match[1]), Number(match[2]));
        },
        dateToTimeText(value) {
            if (!value) {
                return '';
            }
            const date = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(date.getTime())) {
                return '';
            }
            return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        },
        onEditorPreparing(e) {
            if (e.parentType !== 'dataRow' || (e.dataField !== 'start' && e.dataField !== 'end')) {
                return;
            }
            e.editorName = 'dxDateBox';
            e.editorOptions.type = 'time';
            e.editorOptions.displayFormat = 'HH:mm';
            e.editorOptions.pickerType = 'native';
            e.editorOptions.disabled = !this.canWrite || !!e.row?.data?.allDay;
            e.editorOptions.value = this.timeTextToDate(e.value);
            e.editorOptions.onValueChanged = (args) => {
                e.setValue(this.dateToTimeText(args.value));
            };
        },
        setAllDayCellValue(newData, value) {
            newData.allDay = value;
            if (value) {
                newData.start = '00:00';
                newData.end = '00:00';
                return;
            }
            newData.start = '08:00';
            newData.end = '18:00';
        },
        normalizeWindowTime(window, field) {
            if (window.allDay) {
                return;
            }
            window[field] = normalizeTimeInput(window[field]);
        },
        payload() {
            this.dailyWindows.forEach((window) => {
                this.normalizeWindowTime(window, 'start');
                this.normalizeWindowTime(window, 'end');
            });
            this.weeklyWindows.forEach((window) => {
                this.normalizeWindowTime(window, 'start');
                this.normalizeWindowTime(window, 'end');
            });
            return buildReconnectPayload(this);
        },
    },
};
</script>

<style scoped>
.reconnect-section-header {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 0.35rem;
    padding-bottom: 0.35rem;
    border-bottom: 1px solid grey;
}
.reconnect-section-header:not(:first-child) {
    margin-top: 0.5rem;
}
.reconnect-section-header h5 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-color);
}
.reconnect-section-help {
    color: #adb5bd;
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
}
.reconnect-switch {
    display: flex;
    align-items: center;
    white-space: nowrap;
    min-height: calc(1.5em + 0.75rem + 2px);
}


.reconnect-delay-group ::v-deep .input-group-text {
    padding-top: 0;
    padding-bottom: 0;
}
</style>
